import React, { ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';

import useTracking from 'hooks/analytics';

import List from 'components/common/List';
import RectangleTile from 'components/common/tile/RectangleTile';

import { pageSelected } from 'actions/pageView';

import { getContentRowMetadata } from 'support/analytics/analyticsHelper';

import { KitchenSinkRowItem } from 'store-types';
import { PageType } from 'app-types';

interface RectangleRowItem {
  id?: string;
  tag?: {
    key?: string;
    type?: {
      name?: string;
    };
  };
  staticImageUrl?: string;
  displayAbbreviation?: string;
  abbreviation?: string;
  [key: string]: unknown;
}

interface TrackContentEventParams {
  rowTitle?: string | null;
  rowPos?: number | null;
  rowCol?: number | null;
  tag?: string | null;
  pageType?: PageType | null;
}

const trackContentEvent = (
  trackEvent: (eventName: string, eventData: Record<string, unknown>) => void,
  { rowTitle, rowPos, rowCol, tag, pageType }: TrackContentEventParams,
) => {
  const eventData = {
    ...getContentRowMetadata(rowTitle, rowPos, rowCol),
    moduleAction: 'click',
    clickText: `${pageType} carousel - ${tag}`,
  };
  trackEvent(`${pageType} carousel interactions`, eventData);
};

interface RectangleRowProps {
  displayTitle: boolean;
  title?: string | null;
  row: KitchenSinkRowItem;
  className?: string | null;
  rowClassName?: string | null;
  titleClassName?: string | null;
  forwardedRef?: React.Ref<HTMLElement> | null;
  focusIdPrefix?: string | null;
  rowPos?: number | null;
  onEndOfNavigationOverride?: ((index: number) => void) | null;
}

const RectangleRow = ({
  displayTitle,
  title = null,
  row,
  rowClassName = null,
  titleClassName = null,
  forwardedRef = null,
  rowPos = null,
  onEndOfNavigationOverride = null,
}: RectangleRowProps) => {
  const history = useHistory() as MemoryHistory;
  const dispatch = useDispatch();
  const { trackEvent } = useTracking();

  const renderItem = (
    item: RectangleRowItem,
    index: number,
    ref?: React.RefObject<HTMLDivElement>,
  ): ReactNode => {
    // console.log("renderItem info:", item)
    const tag = item?.tag?.key;
    const pageType = item?.tag?.type?.name as PageType;
    const onClick = () => {
      dispatch(pageSelected(tag, pageType as PageType));
      trackContentEvent(trackEvent, {
        rowTitle: title,
        rowPos,
        rowCol: index + 1,
        tag,
        pageType,
      });
      history.push({
        pathname: `/${pageType}/${tag}`,
      });
    };

    return (
      <RectangleTile
        logoSrc={item?.staticImageUrl}
        title={item?.displayAbbreviation || item?.abbreviation}
        focusId={`row-${row.slug}-item-${item?.id}`}
        item={item}
        row={row}
        ref={ref}
        key={item?.id}
        onClick={onClick}
      />
    );
  };

  return (
    <>
      {displayTitle && (
        <div className={classNames(titleClassName, 'font-weight-bold')}>
          {title}
        </div>
      )}
      <List
        focusId={row.slug}
        data={row.data}
        buffer={6}
        className={rowClassName}
        renderItem={renderItem}
        peekRatio={0}
        restorationId={`${row.slug}`}
        ref={forwardedRef}
        onEndOfNavigationOverride={onEndOfNavigationOverride}
      />
    </>
  );
};

export default React.memo(
  React.forwardRef<HTMLElement, Omit<RectangleRowProps, 'forwardedRef'>>((props, ref) => (
    <RectangleRow {...props} forwardedRef={ref} />
  )),
);
